import type { ComponentProps, ReactNode } from "react"
import { cn } from "../../lib"
import {
  Select as BaseSelect,
  Field,
  FieldLabel,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui"
import FieldDescriptionOrError from "./FieldDescriptionOrError"

type Props<TValue extends string> = Omit<
  ComponentProps<typeof BaseSelect<TValue>>,
  "onChange" | "items"
> &
  Pick<ComponentProps<typeof SelectValue>, "placeholder"> &
  Pick<ComponentProps<typeof SelectTrigger>, "variant" | "size"> &
  Pick<ComponentProps<typeof Field>, "orientation"> & {
    label?: string
    onChange?: (value: TValue) => void
    options: {
      label: ReactNode
      value: TValue
    }[]
    className?: string
    fetchMore?: ReactNode
    helperText?: string
    error?: string | null
    isError?: boolean
    contentProps?: Omit<ComponentProps<typeof SelectContent>, "key">
  }

export default function SelectField<TValue extends string>({
  label,
  placeholder,
  onChange,
  options,
  className,
  fetchMore,
  name,
  error,
  isError = false,
  helperText,
  value,
  variant,
  contentProps,
  orientation,
  required,
  ...props
}: Props<TValue>) {
  const handleChange = (value: TValue | null) => {
    if (value === null) {
      return
    }

    onChange?.(value)
  }

  return (
    <Field orientation={orientation} data-invalid={!!error || isError}>
      {label && (
        <FieldLabel className={cn(required && "after:content-['*']")}>
          {label}
        </FieldLabel>
      )}
      <BaseSelect
        onValueChange={handleChange}
        name={name}
        required={required}
        {...props}
        value={value}
        items={options}
      >
        <SelectTrigger
          className={className}
          variant={variant}
          aria-invalid={!!error || isError}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <FieldDescriptionOrError description={helperText} error={error} />
        <SelectContent {...contentProps}>
          <SelectGroup>
            {options.map(({ label, value }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
            {fetchMore}
          </SelectGroup>
        </SelectContent>
      </BaseSelect>
    </Field>
  )
}
